import React from 'react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from 'recharts';
import ContractStatusChart from './ContractStatusChart';

const COLORS = ['#4F46E5', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];

const Charts = ({ data }) => {
  const { monthly, byAgency, contractStatus } = data;
  
  // Formater les données pour le graphique circulaire des agences
  const formatAgencyData = () => {
    if (!byAgency || byAgency.length === 0) return [];
    
    return byAgency.map((item, index) => ({
      name: `Agence ${item.agency}`,
      value: item.count,
      color: COLORS[index % COLORS.length]
    }));
  };
  
  const agencyData = formatAgencyData();
  
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 shadow-md rounded border border-gray-200">
          <p className="font-medium">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {entry.name}: {new Intl.NumberFormat('fr-FR').format(entry.value)} FCFA
            </p>
          ))}
        </div>
      );
    }
    
    return null;
  };
  
  const PieCustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 shadow-md rounded border border-gray-200">
          <p className="font-medium">{payload[0].name}</p>
          <p>{payload[0].value} contrats</p>
        </div>
      );
    }
    
    return null;
  };
  
  return (
    <div className="space-y-6">
      {/* État des contrats - graphique moderne et complet */}
      {contractStatus && (
        <ContractStatusChart data={contractStatus} />
      )}
      
      {/* Graphique à lignes: Évolution mensuelle */}
      {monthly && monthly.length > 0 && (
        <div className="bg-white shadow rounded-lg p-5 hover:shadow-lg transition-shadow">
          <h3 className="text-lg font-semibold mb-4 text-gray-800">Évolution mensuelle</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={monthly}
                margin={{ top: 20, right: 30, left: 20, bottom: 70 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" angle={-45} textAnchor="end" height={70} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="expected" name="Montant Prévu" stroke="#4F46E5" strokeWidth={2} />
                <Line type="monotone" dataKey="received" name="Montant Reçu" stroke="#10B981" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      
      {/* Graphiques pour les agences et montants mensuels */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Graphique à barres: Montants par mois */}
        {monthly && monthly.length > 0 && (
          <div className="bg-white shadow rounded-lg p-5 hover:shadow-lg transition-shadow">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">Montants mensuels</h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={monthly}
                  margin={{ top: 20, right: 30, left: 20, bottom: 70 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" angle={-45} textAnchor="end" height={70} />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="disbursement" name="Décaissé" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="expected" name="Prévu" fill="#4F46E5" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="received" name="Reçu" fill="#10B981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
        
        {/* Graphique circulaire: Répartition par agence */}
        {agencyData.length > 0 && (
          <div className="bg-white shadow rounded-lg p-5 hover:shadow-lg transition-shadow">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">Répartition par agence</h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={agencyData}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {agencyData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<PieCustomTooltip />} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Charts;